// src/pages/MyScoutedProfiles.js
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { ArrowLeft, AlertTriangle, Star, CheckCircle, Clock, Users, Coins, ExternalLink } from 'lucide-react'
import DarkModeToggle from '../components/DarkModeToggle'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function MyScoutedProfiles() {
  const navigate = useNavigate()
  const { token, address } = useAuth()
  const { isDarkMode } = useTheme()
  const [isLoading, setIsLoading] = useState(true)
  const [scoutedProfiles, setScoutedProfiles] = useState([])
  const [userProfile, setUserProfile] = useState(null)

  useEffect(() => {
    fetchData()
  }, [token])

  const fetchData = async () => {
    if (!token) return
    setIsLoading(true)
    try {
      const headers = { Authorization: `Bearer ${token}` }
      
      // Fetch scouted profiles
      const scoutedRes = await axios.get(`${API}/profiles/scouted`, { headers })
      setScoutedProfiles(scoutedRes.data.scouted_profiles || [])
      
      // Fetch user's own profile to check PR balance
      const profileRes = await axios.get(`${API}/profiles/me`, { headers })
      setUserProfile(profileRes.data)
    } catch (err) {
      console.error('Error fetching data:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusBadge = (status, bountyPaid) => {
    if (status === 'done' && bountyPaid) {
      return (
        <Badge className="bg-green-600 text-white">
          <CheckCircle className="w-3 h-3 mr-1" />
          Done - Reward Paid
        </Badge>
      )
    }
    if (status === 'done') {
      return (
        <Badge className="bg-green-500 text-white">
          <CheckCircle className="w-3 h-3 mr-1" />
          Verified
        </Badge>
      )
    }
    if (status === 'claimed') {
      return (
        <Badge className="bg-orange-500 text-white">
          <Clock className="w-3 h-3 mr-1" />
          Pending Review
        </Badge>
      )
    }
    return (
      <Badge className="bg-yellow-500 text-white">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Unclaimed
      </Badge>
    )
  }

  const hasLowBalance = userProfile && userProfile.respect_points < 1

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-white to-purple-50'}`}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/home')}
            className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : ''}`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <DarkModeToggle />
        </div>

        <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className={`text-2xl ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  My Scouted Profiles
                </CardTitle>
                <CardDescription className={isDarkMode ? 'text-gray-400' : ''}>
                  Claimable profiles you've created for others
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Warning if low balance */}
            {hasLowBalance && (
              <div className={`mb-6 p-4 rounded-lg border-2 ${isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-300'}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-6 h-6 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                  <div className="flex-1">
                    <p className={`font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                      ⚠️ You have less than 1 PR in your wallet!
                    </p>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                      Profiles can be claimed anytime - soon or later, avoid losing the reward!
                    </p>
                    <Button
                      onClick={() => navigate('/home')}
                      className="mt-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                    >
                      <Coins className="w-4 h-4 mr-2" />
                      Buy PR
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Summary */}
            {!isLoading && scoutedProfiles.length > 0 && (
              <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      {scoutedProfiles.length}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Scouted</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold text-yellow-600`}>
                      {scoutedProfiles.filter(p => p.status === 'unclaimed').length}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Unclaimed</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold text-orange-600`}>
                      {scoutedProfiles.filter(p => p.status === 'claimed').length}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pending</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold text-green-600`}>
                      {scoutedProfiles.filter(p => p.status === 'done').length}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Completed</p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && scoutedProfiles.length === 0 && (
              <div className="text-center py-12">
                <Star className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  No scouted profiles yet
                </p>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Create claimable profiles for people who deserve recognition!
                </p>
                <Button
                  onClick={() => navigate('/scout-profile')}
                  className="mt-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Scout Someone
                </Button>
              </div>
            )}

            {/* Scouted Profiles List */}
            {!isLoading && scoutedProfiles.length > 0 && (
              <div className="space-y-4">
                {scoutedProfiles.map((profile) => (
                  <div
                    key={profile.username}
                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 hover:border-indigo-500' 
                        : 'bg-white border-gray-200 hover:border-indigo-300'
                    }`}
                    onClick={() => navigate(`/profile/${profile.username}`)}
                    data-testid={`scouted-profile-${profile.username}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      {/* Profile Info */}
                      <div className="flex items-center gap-3">
                        <Avatar className="w-14 h-14 border-2 border-yellow-400">
                          {profile.profile_image ? (
                            <AvatarImage src={profile.profile_image} alt={profile.name} />
                          ) : (
                            <AvatarFallback className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white font-bold text-lg">
                              {profile.name?.charAt(0) || '?'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                              {profile.name}
                            </h3>
                            {getStatusBadge(profile.status, profile.bounty_paid)}
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            @{profile.username}
                          </p>
                        </div>
                      </div>

                      {/* Respect & Reward Info */}
                      <div className="flex items-center gap-4 sm:gap-6">
                        <div className="text-center">
                          <p className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                            {profile.respect_points.toLocaleString()}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            Total PR
                          </p>
                        </div>
                        <div className="text-center">
                          <p className={`text-lg font-bold ${profile.bounty_paid ? 'text-green-600' : 'text-yellow-600'}`}>
                            {profile.reward_amount.toLocaleString()}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {profile.bounty_paid ? 'Reward Received' : '10% Reward'}
                          </p>
                        </div>
                        <ExternalLink className={`w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Create More Button */}
            {!isLoading && scoutedProfiles.length > 0 && (
              <div className="mt-6 text-center">
                <Button
                  onClick={() => navigate('/scout-profile')}
                  variant="outline"
                  className={`${isDarkMode ? 'border-yellow-600 text-yellow-400 hover:bg-yellow-900/30' : 'border-yellow-500 text-yellow-700 hover:bg-yellow-50'}`}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Scout Another Profile
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
